import type { Build } from '@todesktop/shared';
import semver from 'semver';

export const MIN_RUNTIME_VERSION_FOR_DOWNGRADE = '1.4.1-1';

export default function analyzeRelease({
  build,
  latestReleasedBuild,
}: {
  build: Build;
  latestReleasedBuild?: Build;
}): AnalyzeResult {
  const result: AnalyzeResult = {
    downgradeAllowed: Boolean(
      latestReleasedBuild?.todesktopRuntimeVersionUsed &&
      semver.gte(
        latestReleasedBuild.todesktopRuntimeVersionUsed,
        MIN_RUNTIME_VERSION_FOR_DOWNGRADE,
      ),
    ),
    error: '',
    isAlreadyReleased: Boolean(build.releasedAt),
    isAlreadyTheLatest: build.id === latestReleasedBuild?.id,
    isOlderThanLatest: Boolean(
      latestReleasedBuild &&
      latestReleasedBuild.appVersion &&
      build.appVersion &&
      semver.gt(latestReleasedBuild.appVersion, build.appVersion),
    ),
    isReleasable: false,
    isSucceeded: build.status === 'succeeded',
    isTheSameVersionAsLatest: Boolean(
      build.appVersion && build.appVersion === latestReleasedBuild?.appVersion,
    ),
  };

  if (!result.isSucceeded) {
    return {
      ...result,
      error:
        'The build must have completed successfully. ' +
        `Actual build status: ${build.status}.`,
    };
  }

  if (result.isAlreadyTheLatest) {
    return {
      ...result,
      error: 'This build is already the latest released build.',
    };
  }

  if (result.isTheSameVersionAsLatest) {
    return {
      ...result,
      error:
        `This build has the same version as the latest released build ` +
        `(build ID: ${latestReleasedBuild?.id}, version: ${build.appVersion}).`,
    };
  }

  if (!result.downgradeAllowed && result.isAlreadyReleased) {
    return { ...result, error: 'This build has already been released.' };
  }

  if (
    !result.downgradeAllowed &&
    result.isOlderThanLatest &&
    latestReleasedBuild
  ) {
    return {
      ...result,
      error:
        `App version (${build.appVersion}) is not newer than latest released ` +
        `version (${latestReleasedBuild.appVersion}) ` +
        `(build ID: ${latestReleasedBuild.id}).`,
    };
  }

  return { ...result, isReleasable: true };
}

export interface AnalyzeResult {
  downgradeAllowed: boolean;
  error: string;
  isAlreadyReleased: boolean;
  isAlreadyTheLatest: boolean;
  isOlderThanLatest: boolean;
  isReleasable: boolean;
  isSucceeded: boolean;
  isTheSameVersionAsLatest: boolean;
}
